import { memo, useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { useUpdateUserProfile } from 'hooks/UseUpdateUserProfile';
import { copyObject } from 'common/copyObject';
import {
	User,
	initialUserData,
	callGetUserApi,
	callGetUsersSiteDisplayItemsApi,
	UsersDisplayItems,
} from 'requests/RequestUser';
import { FormErrors, initialFormErrors, getErrorMessage } from 'validators/ProfileValidator';
import { Country } from 'consts/CountryConst';
import { Prefecture } from 'consts/PrefectureConst';
import { useSearchAddress } from 'hooks/UseSearchAddress';
import { SelectAddressDialog } from 'components/organisms/SelectAddressDialog';
import { RoutePath } from 'consts/RoutePath';
import { createBirthYearSelectValues } from 'common/birthYearCreator';
import { CompleteDialog } from 'components/organisms/CompleteDialog';
import { UpdateUserReqData } from 'requests/RequestUser';
import { CircularProgress, Backdrop } from '@material-ui/core';
import classes from './override.module.scss';
import { HttpStatus } from 'consts/HttpStatus';
import { convertHalfToFull, convertFullToHalf, convertFullKana } from 'common/CharacterConverter';

type Props = {};
export type UserForm = User & {
	birthYear: string;
	birthMonth: string;
	birthDate: string;
};

export const ProfileEditTemplate = memo((_props: Props) => {
	const [store] = useStore();
	const history = useHistory<UpdateUserReqData>();
	const { searchAddress, zipResponse, zipResponses, isDialogOpen, doSelect, doClose } = useSearchAddress();
	const [userFormData, setUserFormData] = useState<UserForm>({
		birthYear: '',
		birthMonth: '',
		birthDate: '',
		...initialUserData,
	});
	// 変更前と後で比較したいため、変更前の値を保持
	const [prevUserData, setPrevUserData] = useState<User>(initialUserData);
	const isMen = userFormData.sex === 0;
	const isWomen = userFormData.sex === 1;
	const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
	// ユーザー別表示項目取得時の読み込み
	const [displayItemLoading, setDisplayItemLoading] = useState<boolean>(false);
	// ユーザー別必須項目
	const [requiredDisplayItem, setRequiredDisplayItems] = useState<UsersDisplayItems>();
	const handleCompleteDialogOpen = () => {
		setIsCompleteDialogOpen(true);
	};
	const handleCompleteDialogClose = () => {
		setIsCompleteDialogOpen(false);
		history.push(RoutePath.MY_PAGE);
	};
	const [processLoading, setProcessLoading] = useState(false);
	const { update } = useUpdateUserProfile(
		formErrors,
		setFormErrors,
		getErrorMessage,
		actionError,
		setActionError,
		userFormData,
		prevUserData,
		handleCompleteDialogOpen,
		setProcessLoading,
		requiredDisplayItem,
	);

	// ログイン情報を使ってユーザ情報を取得する
	const [getUserLoading, setGetUserLoading] = useState(false);
	useEffect(() => {
		const execute = async () => {
			setGetUserLoading(true);
			const result = await callGetUserApi();
			if (!store.loginUserData.gidpk || result.status !== HttpStatus.OK) {
				history.push(RoutePath.ERROR);
				setGetUserLoading(false);
				setActionError('ユーザ情報を取得できませんでした。');
				return;
			}
			setPrevUserData(result.data);
			const user = copyObject(result.data);
			// 編集中のデータがあれば上書きする
			if (!!history.location.state) {
				const editData = history.location.state.data;
				user.gid = editData.gid;
				user.mailaddress_1 = editData.mailaddress_1;
				user.lastname = editData.lastname;
				user.firstname = editData.firstname;
				user.kana_lastname = editData.kana_lastname;
				user.kana_firstname = editData.kana_firstname;
				user.birth = editData.birth;
				user.nickname = editData.nickname;
				user.sex = editData.sex;
				user.country = editData.country;
				user.zip = editData.zip;
				user.tel = editData.tel;
				user.pref_cd = editData.pref_cd;
				user.address_2 = editData.address_2;
				user.address_3 = editData.address_3;
				user.address_4 = editData.address_4;
				user.family_type = editData.family_type;
				user.agree_flg = editData.agree_flg;
				user.data_util_agree_flg = editData.data_util_agree_flg;
			}
			const birth = user.birth ? new Date(user.birth) : undefined;
			setUserFormData({
				birthYear: !!birth ? birth.getFullYear().toString() : '',
				birthMonth: !!birth ? (birth.getMonth() + 1).toString() : '',
				birthDate: !!birth ? birth.getDate().toString() : '',
				...user,
				pref_cd: user.pref_cd.trim(),
				country: user.country.trim(),
			});
			// ユーザ情報取得したタイミングでもう一度プレースホルダ判定をする
			setIsPlaceHolders((prev) => ({
				...prev,
				pref_cd: user.pref_cd ? !user.pref_cd.trim() : true,
				birthYear: !birth,
				birthMonth: !birth,
				birthDate: !birth,
				country: user.country ? !user.country.trim() : true,
			}));
			setGetUserLoading(false);
		};
		execute();
	}, []);

	// ユーザー別表示項目取得API
	useEffect(() => {
		const execute = async () => {
			setDisplayItemLoading(true);

			const result = await callGetUsersSiteDisplayItemsApi();
			if (result.status === HttpStatus.OK) {
				setRequiredDisplayItems(result.data);
			}
			setDisplayItemLoading(false);
		};
		execute();
	}, []);

	// 各入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleChangeRadioValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: Number(event.target.value) }));
	};
	const handleChangeCheckValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: event.target.checked ? 1 : 0 }));
	};
	const [isPlaceHolders, setIsPlaceHolders] = useState({
		pref_cd: !userFormData.pref_cd,
		birthYear: !userFormData.birthYear,
		birthMonth: !userFormData.birthMonth,
		birthDate: !userFormData.birthDate,
		country: !userFormData.country,
	});
	const handleChangeSelectValueWithPlaceHolder = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setIsPlaceHolders((prev) => ({ ...prev, [event.target.name]: !event.target.value }));
		setUserFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleBlurConvertHalfToFullValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: convertHalfToFull(event.target.value) }));
	};
	const handleBlurConvertFullToHalfValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: convertFullToHalf(event.target.value) }));
	};
	const handleBlurConvertFullKanaValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: convertFullKana(event.target.value) }));
	};

	// 住所検索関連
	const search = async () => {
		await searchAddress(userFormData.zip);
	};
	useEffect(() => {
		if (zipResponse) {
			// 入力欄に格納
			setUserFormData((prev) => ({
				...prev,
				zip: zipResponse.zipcode,
				pref_cd: zipResponse.prefcode,
				address_2: zipResponse.address2,
				address_3: zipResponse.address3,
			}));
			setIsPlaceHolders((prev) => ({ ...prev, pref_cd: !zipResponse.prefcode }));
		}
	}, [zipResponse]);

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenIDマイページ｜プロフィール変更';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<main>
			<Backdrop className={classes.backdrop} open={getUserLoading || displayItemLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<section className="section mypage" ref={scrollRef}>
				<div className="breadcrumb">
					<ul>
						<li>
							<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
						</li>
						<li>
							<Link to={RoutePath.ACCOUNT_INFO}>登録情報</Link>
						</li>
						<li>プロフィール変更</li>
					</ul>
				</div>

				<h1 className="page-head">プロフィール変更</h1>

				<div className="section-in">
					{isFormError || isActionError ? (
						<div className="error-box" ref={errorRef}>
							{isActionError ? <p>{actionError}</p> : null}
							{isFormError ? (
								<>
									<p>※下記項目は全て必須となります。</p>
									<div className="error-box-in">
										{formErrors.gid !== '' ? <p>【GakkenID】{formErrors.gid}</p> : null}
										{formErrors.mailaddress_1 !== '' ? <p>【メールアドレス】{formErrors.mailaddress_1}</p> : null}
										{formErrors.lastname !== '' || formErrors.firstname !== '' ? (
											<p>【お名前】{formErrors.lastname || formErrors.firstname}</p>
										) : null}
										{formErrors.kana_lastname !== '' || formErrors.kana_firstname !== '' ? (
											<p>【お名前（カナ）】{formErrors.kana_lastname || formErrors.kana_firstname}</p>
										) : null}
										{formErrors.birthYear !== '' || formErrors.birthMonth !== '' || formErrors.birthDate !== '' ? (
											<p>【生年月日】{formErrors.birthYear || formErrors.birthMonth || formErrors.birthDate}</p>
										) : null}
										{formErrors.sex !== '' ? <p>【性別】{formErrors.sex}</p> : null}
										{formErrors.tel !== '' ? <p>【電話番号】{formErrors.tel}</p> : null}
										{formErrors.zip !== '' ? <p>【郵便番号】{formErrors.zip}</p> : null}
										{formErrors.pref_cd !== '' ? <p>【都道府県】{formErrors.pref_cd}</p> : null}
										{formErrors.address_2 !== '' ? <p>【市区町村】{formErrors.address_2}</p> : null}
										{formErrors.address_3 !== '' ? <p>【町名・番地】{formErrors.address_3}</p> : null}
										{formErrors.address_4 !== '' ? <p>【マンション名】{formErrors.address_4}</p> : null}
										{formErrors.nickname !== '' ? <p>【ニックネーム】{formErrors.nickname}</p> : null}
										{formErrors.country !== '' ? <p>【国】{formErrors.country}</p> : null}
									</div>
								</>
							) : null}
						</div>
					) : null}
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="gid">GakkenID</label>
							<span className="required">必須</span>
							<span className="annotation">※半角英数8文字以上</span>
						</div>
						<div className="form-input width50">
							<input
								id="gid"
								type="text"
								name="gid"
								placeholder="gakken1946"
								value={userFormData.gid}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertFullToHalfValue}
							/>
						</div>
						<p className="error">{formErrors.gid}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="mailaddress_1">メールアドレス</label>
							<span className="required">必須</span>
						</div>
						<div className="form-input width50">
							<input
								id="mailaddress_1"
								type="text"
								name="mailaddress_1"
								placeholder="sample@gakken.com"
								value={userFormData.mailaddress_1}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertFullToHalfValue}
							/>
						</div>
						<p className="error">{formErrors.mailaddress_1}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="lastname">お名前（全角）</label>
							<span className="required">必須</span>
							<span className="annotation">※全角</span>
						</div>
						<div className="form-input width50">
							<input
								id="lastname"
								type="text"
								name="lastname"
								placeholder="姓"
								value={userFormData.lastname}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertHalfToFullValue}
							/>
						</div>
						<p className="error">{formErrors.lastname}</p>
						<div className="form-input width50">
							<input
								type="text"
								name="firstname"
								placeholder="名"
								value={userFormData.firstname}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertHalfToFullValue}
							/>
						</div>
						<p className="error">{formErrors.firstname}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="kana_lastname">お名前（カナ）</label>
							{requiredDisplayItem &&
							(requiredDisplayItem.kana_firstname === 1 || requiredDisplayItem.kana_lastname === 1) ? (
								<span className="required">必須</span>
							) : null}
							<span className="annotation">※全角カタカナ</span>
						</div>
						<div className="form-input width50">
							<input
								id="kana_lastname"
								type="text"
								name="kana_lastname"
								placeholder="セイ"
								value={userFormData.kana_lastname}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertFullKanaValue}
							/>
						</div>
						<p className="error">{formErrors.kana_lastname}</p>
						<div className="form-input width50">
							<input
								type="text"
								name="kana_firstname"
								placeholder="メイ"
								value={userFormData.kana_firstname}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertFullKanaValue}
							/>
						</div>
						<p className="error">{formErrors.kana_firstname}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="birthYear">生年月日</label>
							<span className="required">必須</span>
						</div>
						<div className="form-input width50">
							<div className="select-box">
								<select
									id="birthYear"
									name="birthYear"
									className={isPlaceHolders.birthYear ? classes.placeholderOption : ''}
									value={userFormData.birthYear}
									onChange={handleChangeSelectValueWithPlaceHolder}
								>
									<option value="" className={classes.placeholderOption}>
										年
									</option>
									{createBirthYearSelectValues().map((v) => {
										return (
											<option value={`${v}`} className={classes.defaultOption}>
												{v}
											</option>
										);
									})}
								</select>
							</div>
						</div>
						<p className="error">{formErrors.birthYear}</p>
						<div className="form-input width50">
							<div className="select-box">
								<label htmlFor="birthMonth" className="visually-hidden">
									月
								</label>
								<select
									id="birthMonth"
									name="birthMonth"
									className={isPlaceHolders.birthMonth ? classes.placeholderOption : ''}
									value={userFormData.birthMonth}
									onChange={handleChangeSelectValueWithPlaceHolder}
								>
									<option value="" className={classes.placeholderOption}>
										月
									</option>
									{new Array(12).fill(0).map((v, i) => {
										return (
											<option value={`${i + 1}`} className={classes.defaultOption}>
												{i + 1}
											</option>
										);
									})}
								</select>
							</div>
						</div>
						<p className="error">{formErrors.birthMonth}</p>
						<div className="form-input width50">
							<div className="select-box">
								<label htmlFor="birthDate" className="visually-hidden">
									日
								</label>
								<select
									id="birthDate"
									name="birthDate"
									className={isPlaceHolders.birthDate ? classes.placeholderOption : ''}
									value={userFormData.birthDate}
									onChange={handleChangeSelectValueWithPlaceHolder}
								>
									<option value="" className={classes.placeholderOption}>
										日
									</option>
									{new Array(31).fill(0).map((v, i) => {
										return (
											<option value={`${i + 1}`} className={classes.defaultOption}>
												{i + 1}
											</option>
										);
									})}
								</select>
							</div>
						</div>
						<p className="error">{formErrors.birthDate}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="sex">性別</label>
							<input className="visually-hidden" type="text" id="sex"></input>
							{requiredDisplayItem && requiredDisplayItem.sex === 1 ? <span className="required">必須</span> : null}
						</div>
						<div className="form-input">
							<div className="radio-box">
								<ul>
									<li>
										<label className={`radio-label${isMen ? ' is-active' : ''}`} htmlFor="male">
											<input
												type="radio"
												id="male"
												name="sex"
												value={0}
												checked={isMen}
												onChange={handleChangeRadioValue}
											/>
											男
										</label>
									</li>
									<li>
										<label className={`radio-label${isWomen ? ' is-active' : ''}`} htmlFor="female">
											<input
												type="radio"
												id="female"
												name="sex"
												value={1}
												checked={isWomen}
												onChange={handleChangeRadioValue}
											/>
											女
										</label>
									</li>
								</ul>
							</div>
						</div>
						<p className="error">{formErrors.sex}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="tel">電話番号</label>
							{requiredDisplayItem && requiredDisplayItem.tel === 1 ? (
								<span className="required">必須</span>
							) : (
								<span className="optional">（任意）</span>
							)}
							<span className="annotation">※半角数字</span>
						</div>
						<div className="form-input width50">
							<input
								id="tel"
								type="text"
								name="tel"
								placeholder="12345678910"
								value={userFormData.tel}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertFullToHalfValue}
							/>
						</div>
						<p className="error">{formErrors.tel}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="zip">郵便番号</label>
							{requiredDisplayItem && requiredDisplayItem.zip === 1 ? (
								<span className="required">必須</span>
							) : (
								<span className="optional">（任意）</span>
							)}
							<span className="annotation">※半角数字 ハイフンなし</span>
						</div>
						<div className="form-input width50 form-input-address">
							<input
								id="zip"
								type="text"
								name="zip"
								placeholder="1234567"
								value={userFormData.zip}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertFullToHalfValue}
							/>
							<div className="address-search-btn">
								<button onClick={search}>住所検索</button>
							</div>
						</div>
						<p className="error">{formErrors.zip}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="pref_cd">都道府県</label>
							{requiredDisplayItem && requiredDisplayItem.pref_cd === 1 ? (
								<span className="required">必須</span>
							) : (
								<span className="optional">（任意）</span>
							)}
						</div>
						<div className="form-input width50">
							<div className="select-box">
								<select
									id="pref_cd"
									name="pref_cd"
									className={isPlaceHolders.pref_cd ? classes.placeholderOption : ''}
									value={userFormData.pref_cd}
									onChange={handleChangeSelectValueWithPlaceHolder}
								>
									<option value="" className={classes.placeholderOption}>
										都道府県を選択
									</option>
									{Object.values(Prefecture).map((v) => {
										return (
											<option value={v.key} className={classes.defaultOption}>
												{v.name}
											</option>
										);
									})}
								</select>
							</div>
						</div>
						<p className="error">{formErrors.pref_cd}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="address_2">市区町村</label>
							{requiredDisplayItem && requiredDisplayItem.address_2 === 1 ? (
								<span className="required">必須</span>
							) : (
								<span className="optional">（任意）</span>
							)}
						</div>
						<div className="form-input width50">
							<input
								id="address_2"
								type="text"
								name="address_2"
								placeholder="品川区"
								value={userFormData.address_2}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertHalfToFullValue}
							/>
						</div>
						<p className="error">{formErrors.address_2}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="address_3">町名・番地</label>
							{requiredDisplayItem && requiredDisplayItem.address_3 === 1 ? (
								<span className="required">必須</span>
							) : (
								<span className="optional">（任意）</span>
							)}
							<span className="annotation">※全角</span>
						</div>
						<div className="form-input width50">
							<input
								id="address_3"
								type="text"
								name="address_3"
								placeholder="西五反田２−１１−８"
								value={userFormData.address_3}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertHalfToFullValue}
							/>
						</div>
						<p className="error">{formErrors.address_3}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="address_4">マンション名</label>
							{requiredDisplayItem && requiredDisplayItem.address_4 === 1 ? (
								<span className="required">必須</span>
							) : (
								<span className="optional">（任意）</span>
							)}
							<span className="annotation">※全角</span>
						</div>
						<div className="form-input width50">
							<input
								id="address_4"
								type="text"
								name="address_4"
								placeholder="○○マンション１０１号室"
								value={userFormData.address_4}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertHalfToFullValue}
							/>
						</div>
						<p className="error">{formErrors.address_4}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="nickname">ニックネーム</label>
							{requiredDisplayItem && requiredDisplayItem.nickname === 1 ? (
								<span className="required">必須</span>
							) : (
								<span className="optional">（任意）</span>
							)}
							<span className="annotation">※全角</span>
						</div>
						<div className="form-input width50">
							<input
								id="nickname"
								type="text"
								name="nickname"
								placeholder="がっけんたろう"
								value={userFormData.nickname}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertHalfToFullValue}
							/>
						</div>
						<p className="error">{formErrors.nickname}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="country">国</label>
							{requiredDisplayItem && requiredDisplayItem.country === 1 ? (
								<span className="required">必須</span>
							) : (
								<span className="optional">（任意）</span>
							)}
						</div>
						<div className="form-input width50">
							<div className="select-box">
								<select
									id="country"
									name="country"
									className={isPlaceHolders.country ? classes.placeholderOption : ''}
									value={userFormData.country}
									onChange={handleChangeSelectValueWithPlaceHolder}
								>
									<option value="" className={classes.placeholderOption}>
										国を選択
									</option>
									{Object.values(Country).map((v) => {
										return (
											<option value={v.key} className={classes.defaultOption}>
												{v.name}
											</option>
										);
									})}
								</select>
							</div>
						</div>
						<p className="error">{formErrors.country}</p>
					</div>
					<div className="form-box">
						<div className="form-input">
							<div className="check-box">
								<ul>
									<li>
										<label
											className={`check-label ${userFormData.data_util_agree_flg === 1 ? 'is-active' : ''}`}
											htmlFor="agree"
										>
											<input
												type="checkbox"
												id="agree"
												name="data_util_agree_flg"
												checked={userFormData.data_util_agree_flg === 1}
												onChange={handleChangeCheckValue}
											/>
											サービス向上のためデータ活用に同意する
										</label>
									</li>
								</ul>
							</div>
						</div>
					</div>

					<div className="btn-box">
						<div className="btn gr">
							{processLoading ? <CircularProgress /> : <button onClick={update}>プロフィールを変更する</button>}
						</div>
					</div>
				</div>
			</section>
			{/* 住所選択ダイアログ */}
			<SelectAddressDialog isOpen={isDialogOpen} data={zipResponses} doSelect={doSelect} doClose={doClose} />
			{/* 完了ダイアログ */}
			<CompleteDialog isOpen={isCompleteDialogOpen} doTrans={handleCompleteDialogClose} />
		</main>
	);
});
